import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Markdown from "../../site/Markdown";
import { useSeo } from "../../site/ui";
import { LaonaMark } from "../LaonaApp";
import { projectApi, StatusTag, won } from "./LaonaProjects";
import MakeTab, { isLocked } from "./LaonaMake";
import PlanPanel, { planOver, planUsed } from "./LaonaPlan";

/* =========================================================
   라오나봇 AI 설계 모드 — 수동 설계와 같은 연결도 화면(PinWire) + 떠 있는 라오나봇 대화창.
   대화로 기능을 정하면 라오나봇이 모듈을 추천하고, [적용]을 누르면 서버가 규칙으로 배선해
   연결도 · 브레드보드에 바로 나온다. 적용 · 비슷한 모듈 바꾸기는 API 를 쓰지 않아 비용이 없다.
   (백엔드 app/laona_projects.py · app/laona_wiring.py, 도구 app/laona_tool/pinwire.html 의 loadProject)
   ========================================================= */

const EXAMPLES = [
  "화분 흙이 마르면 자동으로 물을 주고 싶어요",
  "현관에 사람이 오면 불이 켜지고 화면에 시간이 나오게 하고 싶어요",
  "방 온도 · 습도를 작은 화면에 보여 주는 장치가 필요해요",
];
const WIN_KEY = "laona.chatwin.v1";
const MIN_W = 320, MIN_H = 360;

function loadWin() {
  /* 처음에는 오른쪽, 연결도 도구 줄(되돌리기 · 넷리스트) 아래에 — 도구 버튼을 가리지 않게 */
  const top = 205;
  const w = Math.min(420, window.innerWidth - 24), h = Math.min(660, window.innerHeight - top - 24);
  const def = { x: window.innerWidth - w - 24, y: top, w, h, min: false };
  try {
    const s = JSON.parse(localStorage.getItem(WIN_KEY));
    if (s && Number.isFinite(s.x)) return clampWin({ ...def, ...s });
  } catch { /* 저장 안 됨 */ }
  return clampWin(def);
}
function clampWin(s) {
  const vw = window.innerWidth, vh = window.innerHeight;
  const w = Math.max(MIN_W, Math.min(s.w, vw - 16)), h = Math.max(MIN_H, Math.min(s.h, vh - 16));
  return { ...s, w, h, x: Math.max(8, Math.min(s.x, vw - w - 8)), y: Math.max(8, Math.min(s.y, vh - 48)) };
}

/* 끌어서 옮기기 · 크기 바꾸기 — 포인터 캡처라 연결도(iframe) 위를 지나가도 끊기지 않는다 */
function useDrag(onMove, onEnd) {
  return (e) => {
    if (e.button !== 0) return;
    const el = e.currentTarget, sx = e.clientX, sy = e.clientY;
    el.setPointerCapture(e.pointerId);
    document.documentElement.classList.add("lb-dragging");
    const move = (ev) => onMove(ev.clientX - sx, ev.clientY - sy);
    const up = () => {
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", up);
      document.documentElement.classList.remove("lb-dragging");
      onEnd();
    };
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
  };
}

function ModThumb({ mod }) {
  return mod?.image
    ? <img className="lb-rec__img" src={mod.image} alt="" />
    : <span className="lb-rec__img lb-rec__img--empty" aria-hidden="true" />;
}

/* 추천 모듈 한 줄 — [적용] / 적용됨, [비슷한 모듈] 로 다른 것 고르기 (서버 계산, 비용 없음) */
function RecItem({ p, item, applied, catalog, act, msgId }) {
  const [alts, setAlts] = useState(null);
  const [open, setOpen] = useState(false);
  const mod = catalog[item.id];

  async function toggleAlts() {
    if (!open && !alts) {
      try {
        const d = await projectApi(`/${p.id}/similar?id=${encodeURIComponent(item.id)}`);
        setAlts(d.items);
      } catch (e) {
        setAlts([]);
      }
    }
    setOpen((v) => !v);
  }

  return (
    <li className="lb-rec__item">
      <div className="lb-rec__row">
        <ModThumb mod={mod} />
        <div className="lb-rec__txt">
          <b>{item.id}</b>
          <span>{item.role || mod?.name || ""}</span>
          {item.from && <em className="lb-rec__from">원래 추천 {item.from} 에서 바꿈</em>}
        </div>
        <div className="lb-rec__acts">
          {applied.has(item.id) ? (
            <span className="lb-rec__ok">적용됨</span>
          ) : (
            <button type="button" className="lb-btn lb-btn--sm lb-btn--primary" disabled={act.busy}
                    onClick={() => act.apply({ add: [item] })}>적용</button>
          )}
          <button type="button" className="lb-btn lb-btn--sm" onClick={toggleAlts} aria-expanded={open}>
            {open ? "닫기" : "비슷한 모듈"}
          </button>
        </div>
      </div>
      {open && (
        <ul className="lb-rec__alts">
          {alts === null && <li className="lb-rec__note">찾는 중…</li>}
          {alts && !alts.length && <li className="lb-rec__note">비슷한 모듈이 없습니다.</li>}
          {alts && alts.map((a) => (
            <li key={a.id} className="lb-rec__row">
              <ModThumb mod={catalog[a.id]} />
              <div className="lb-rec__txt">
                <b>{a.id}</b>
                <span>{a.name}{a.why ? ` — ${a.why}` : ""}</span>
              </div>
              <div className="lb-rec__acts">
                {applied.has(a.id) ? <span className="lb-rec__ok">적용됨</span> : (
                  <button type="button" className="lb-btn lb-btn--sm" disabled={act.busy}
                          onClick={() => act.apply({
                            ...(applied.has(item.id) ? { replace: { [item.id]: a.id } } : { add: [{ id: a.id, role: item.role }] }),
                            /* 대화의 추천 카드라면 그 줄도 새 모듈로 바꿔 둔다 (화면 · 서버 모두) */
                            ...(msgId ? { swap: { message_id: msgId, from_id: item.id, to_id: a.id } } : {}),
                          })}>
                    이걸로
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function Recommend({ p, list, applied, catalog, act, msgId }) {
  if (!list || !list.length) return null;
  const rest = list.filter((x) => !applied.has(x.id));
  return (
    <div className="lb-rec">
      <div className="lb-rec__head">
        <span>추천 모듈</span>
        {rest.length > 1 && (
          <button type="button" className="lb-btn lb-btn--sm lb-btn--primary" disabled={act.busy}
                  onClick={() => act.apply({ add: rest })}>모두 적용</button>
        )}
      </div>
      <ul className="lb-rec__list">
        {list.map((it) => <RecItem key={it.id} p={p} item={it} applied={applied} catalog={catalog} act={act} msgId={msgId} />)}
      </ul>
    </div>
  );
}

/* [내 프로젝트로 저장] — 라오나봇이 설계 · 기능이 모두 정해졌다고 보고 저장을 물으면(propose_save) 그 답 아래에 띄운다.
   기능 명세가 없거나 설계가 바뀌었으면 먼저 명세를 정리하고(사용량이 든다), 저장하면 설계가 잠기고 펌웨어 개발의뢰가 생긴다 */
function SaveOffer({ p, onSaved, onUsage, reload }) {
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  async function save() {
    if (!window.confirm("이 설계를 내 프로젝트로 저장할까요?\n저장하면 대화 · 모듈 바꾸기가 잠기고, 기능 명세가 펌웨어 기능정의서가 됩니다.\n"
      + "내 프로젝트에서 [주문하기]로 부품과 펌웨어 개발 · 빌드를 함께 주문할 수 있습니다.")) return;
    setError("");
    try {
      const prod = await projectApi(`/${p.id}/production`);
      if (!prod.spec || prod.spec_stale) {
        setBusy("spec");
        const d = await projectApi(`/${p.id}/spec`, { method: "POST" });
        if (d.usage) onUsage(d.usage);
      }
      setBusy("save");
      const d = await projectApi(`/${p.id}/approve`, { method: "POST" });
      onSaved(d.status);
    } catch (e) {
      setError(e.message);
      if (e.status === 402) await reload();
    } finally {
      setBusy("");
    }
  }
  return (
    <div className="lb-save-offer">
      <button type="button" className="lb-btn lb-btn--sm lb-btn--primary" onClick={save} disabled={!!busy}>
        {busy === "spec" ? "기능 명세 정리하는 중… (1분 정도)" : busy === "save" ? "저장하는 중…" : "내 프로젝트로 저장"}
      </button>
      <span className="lb-mk-note">저장하면 내 프로젝트에서 부품과 펌웨어 개발 · 빌드를 함께 주문합니다.</span>
      {error && <p className="lb-error">{error}</p>}
    </div>
  );
}

function ChatTab({ p, msgs, setMsgs, onUsage, applied, catalog, act, locked, reload, onSaved }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  /* 저장을 묻는 답은 마지막 것만 버튼을 단다 */
  const lastOffer = [...msgs].reverse().find((m) => m.role !== "user" && m.propose_save);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs, busy]);

  async function send(message) {
    const m = (message ?? text).trim();
    if (!m || busy) return;
    setBusy(true);
    setError("");
    setMsgs((list) => [...list, { id: `u${Date.now()}`, role: "user", text: m }]);
    setText("");
    try {
      const d = await projectApi(`/${p.id}/chat`, { method: "POST", body: JSON.stringify({ message: m }) });
      setMsgs((list) => [...list, { id: d.id ?? `a${Date.now()}`, role: "assistant", text: d.reply, krw: d.krw, recommend: d.recommend,
                                    propose_save: d.propose_save }]);
      onUsage(d.usage);
    } catch (e) {
      setMsgs((list) => list.slice(0, -1)); // 보내지 못한 메시지는 입력칸으로 되돌린다
      setText(m);
      if (e.status === 402) await reload(); // 무료 한도 — 이용료 결제 / 직접 만들기 안내로 바뀐다
      else setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="lb-win__log" aria-live="polite">
        <div className="lb-msg lb-msg--bot">
          <div className="lb-msg__body">
            <p>안녕하세요, 라오나봇입니다. 어떤 것을 만들고 싶으세요? 생각나는 대로 말씀해 주시면
              질문으로 기능을 정리하고, 알맞은 모듈을 추천해 연결도에 바로 놓아 드릴게요.</p>
          </div>
        </div>
        {msgs.length === 0 && (
          <div className="lb-chat__examples">
            {EXAMPLES.map((ex) => (
              <button key={ex} type="button" className="lb-chip" onClick={() => send(ex)} disabled={busy}>{ex}</button>
            ))}
          </div>
        )}
        {msgs.map((m) => (
          <div key={m.id} className={`lb-msg ${m.role === "user" ? "lb-msg--me" : "lb-msg--bot"}`}>
            <div className="lb-msg__body">
              {m.role === "user" ? <p>{m.text}</p> : <Markdown source={m.text || ""} />}
            </div>
            {m.role !== "user" && (
              <Recommend p={p} list={m.recommend} applied={applied} catalog={catalog} act={act}
                         msgId={typeof m.id === "number" ? m.id : undefined} />
            )}
            {m === lastOffer && !locked && p.ai && <SaveOffer p={p} onSaved={onSaved} onUsage={onUsage} reload={reload} />}
            {m.role !== "user" && m.krw > 0 && <span className="lb-msg__cost">{won(m.krw)}</span>}
          </div>
        ))}
        {busy && (
          <div className="lb-msg lb-msg--bot">
            <div className="lb-msg__body lb-msg__typing">생각하는 중…</div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      {error && <p className="lb-error lb-win__err">{error}</p>}
      {!p.ai ? (
        <p className="lb-win__locked">라오나봇 AI 설계 서비스는 지금 준비 중이라 대화를 잠시 멈췄습니다. 연결도는 그대로 쓸 수 있습니다.</p>
      ) : !locked && planOver(p) ? (
        <PlanPanel p={p} plan={p.plan} bank={p.pay?.bank} onChanged={reload} />
      ) : locked ? (
        <p className="lb-win__locked">
          내 프로젝트에 저장해 대화가 잠겼습니다. <Link to="/laonabot/projects">내 프로젝트</Link>에서 주문할 수 있고,
          바꾸려면 [제작] 탭에서 '설계 다시 열기'를 누르세요.
        </p>
      ) : (
      <form className="lb-win__form" onSubmit={(e) => { e.preventDefault(); send(); }}>
        <textarea
          value={text}
          rows={2}
          maxLength={2000}
          placeholder="만들고 싶은 것을 적어 주세요 (Enter 보내기 · Shift+Enter 줄바꿈)"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); }
          }}
          disabled={busy}
        />
        <button type="submit" className="lb-btn lb-btn--primary" disabled={busy || !text.trim()}>보내기</button>
      </form>
      )}
    </>
  );
}

function DesignTab({ p, design, catalog, act, locked, toMake }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const mods = design?.modules || [];
  const applied = new Set(mods.map((m) => m.id));

  async function aiCheck() {
    setBusy(true);
    setError("");
    try {
      const d = await projectApi(`/${p.id}/design`, { method: "POST" });
      act.setDesign(d.design, d.usage);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!mods.length) {
    return (
      <div className="lb-win__pad">
        <p className="lb-design__hint">
          아직 적용한 모듈이 없습니다. [대화] 탭에서 라오나봇이 추천한 모듈을 [적용]하면
          연결도에 놓이고 배선까지 자동으로 됩니다.
        </p>
      </div>
    );
  }
  return (
    <div className="lb-win__pad lb-win__scroll">
      {locked && <p className="lb-win__locked">승인된 설계입니다 — 바꾸려면 [제작] 탭에서 '설계 다시 열기'를 누르세요.</p>}
      {design.title && <h3 className="lb-design__title">{design.title}</h3>}
      {design.summary && <p className="lb-win__sum">{design.summary}</p>}
      <div className="lb-dsec">
        <h3>적용한 모듈 ({mods.length})</h3>
        <ul className="lb-rec__list">
          {mods.map((m) => (
            <RecItem key={m.id} p={p} item={m} applied={applied} catalog={catalog}
                     act={{ ...act, apply: act.apply }} />
          ))}
        </ul>
        <div className="lb-win__mods">
          {mods.map((m) => (
            <button key={m.id} type="button" className="lb-chip lb-chip--x" disabled={act.busy}
                    onClick={() => act.apply({ remove: [m.id] })} title={`${m.id} 빼기`}>
              {m.id} ✕
            </button>
          ))}
        </div>
      </div>
      <div className="lb-dsec">
        <h3>부품 합계</h3>
        <p className="lb-win__total">
          {won(design.bom?.parts_total)}
          {design.bom?.unpriced > 0 && <span> (가격 확인 중 {design.bom.unpriced}개 제외)</span>}
        </p>
      </div>
      <div className="lb-dsec">
        <h3>배선 {design.connections?.length || 0}개 · {design.source === "ai" ? "AI 점검" : "자동 배선"}</h3>
        <p className="lb-win__note">연결도와 브레드보드에서 바로 확인할 수 있습니다.</p>
      </div>
      {design.cautions?.length > 0 && (
        <div className="lb-dsec lb-dsec--warn">
          <h3>확인할 점</h3>
          <ul>{design.cautions.map((t) => <li key={t}>{t}</li>)}</ul>
        </div>
      )}
      {design.problems?.length > 0 && (
        <div className="lb-dsec lb-dsec--warn">
          <h3>자동 점검에서 뺀 배선</h3>
          <ul>{design.problems.map((t) => <li key={t}>{t}</li>)}</ul>
        </div>
      )}
      {!locked && p.ai && <div className="lb-next">
        <b>AI 로 배선 점검</b>
        <p>자동 배선은 규칙으로 핀을 정합니다. 기능에 맞게 핀 배정 · 주의 사항까지 라오나봇이 다시 검토하게 하려면 누르세요 (사용량이 듭니다, 1분 정도).</p>
        <button type="button" className="lb-btn lb-btn--sm" onClick={aiCheck} disabled={busy || planOver(p)}>
          {busy ? "점검하는 중…" : "AI 로 배선 점검 · 정리"}
        </button>
        {error && <p className="lb-error">{error}</p>}
      </div>}
      <div className="lb-next">
        <b>다음 단계</b>
        <p>설계가 정해지면 [제작] 탭에서 기능 명세를 정리 · 승인하고 펌웨어를 만듭니다(이용료 결제 후). 그다음 부품 결산 주문을 하면 키트와 결과물을 받습니다.</p>
        <button type="button" className="lb-btn lb-btn--sm" onClick={toMake}>제작 탭으로</button>
      </div>
    </div>
  );
}

/* 떠 있는 라오나봇 대화창 */
function PlanBadge({ p }) {
  const plan = p.plan || {};
  if (plan.paid) return <span className="lb-win__cost" title="이용료 결제 — 라오나봇과 이어서 대화 · 설계합니다">이용료 결제됨</span>;
  return (
    <span className="lb-win__cost" title={`무료 사용 한도(회원마다 하나, 다른 미결제 프로젝트 포함) — 넘으면 이용료 ${won(plan.fee)}를 결제하고 이어서 대화하거나 직접 만들기로 넘어갑니다`}>
      무료 {won(Math.min(planUsed(p), plan.free_krw))}/{won(plan.free_krw)}
    </span>
  );
}

/* 직접 만들기로 넘긴 프로젝트 — 수동 프로젝트로 안내. 옮겨 둔 수동 프로젝트를 지웠으면 다시 옮긴다 */
function MovedPanel({ p }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function again() {
    setBusy(true);
    setError("");
    try {
      const d = await projectApi(`/${p.id}/manual`, { method: "POST" });
      navigate(`/laonabot/wiring?manual=${d.manual_id}`);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }
  return (
    <div className="lb-deposit lb-deposit--win">
      <h2>직접 만들기로 넘긴 프로젝트입니다</h2>
      <p>
        연결 작업 · 브레드보드 배선 · 부품 주문은 수동 프로젝트에서 이어서 할 수 있습니다.
        펌웨어는 Arduino IDE 나 PlatformIO 로 스스로 작성 · 빌드해 보드에 올려 주세요.
      </p>
      {p.manual_id ? (
        <Link to={`/laonabot/wiring?manual=${p.manual_id}`} className="lb-btn lb-btn--sm lb-btn--primary">수동 프로젝트 열기</Link>
      ) : (
        <button type="button" className="lb-btn lb-btn--sm lb-btn--primary" disabled={busy} onClick={again}>
          {busy ? "옮기는 중…" : "수동 프로젝트로 다시 옮기기"}
        </button>
      )}
      {error && <p className="lb-error">{error}</p>}
    </div>
  );
}

function ChatWindow({ p, reload, onUsage, frameRef }) {
  const [win, setWin] = useState(loadWin);
  const [tab, setTab] = useState("chat");
  const [msgs, setMsgs] = useState(p.messages);
  const [design, setDesignState] = useState(p.design);
  const [catalog, setCatalog] = useState({});
  const [status, setStatus] = useState(p.status);
  const locked = isLocked(status);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState("");
  const start = useRef(win);

  useEffect(() => {
    try { localStorage.setItem(WIN_KEY, JSON.stringify(win)); } catch { /* 저장 안 됨 */ }
  }, [win]);
  useEffect(() => {
    const onResize = () => setWin((w) => clampWin(w));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  /* 모듈 이름 · 그림 — 연결도와 같은 라이브러리 (회원: 공개 모듈, 관리자: 전체) */
  useEffect(() => {
    fetch("/api/laona/modules", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setCatalog(Object.fromEntries(d.items.map((m) => [m.id, m]))))
      .catch(() => {});
  }, []);

  const notifyTool = useCallback(() => {
    frameRef.current?.contentWindow?.postMessage({ type: "laona:reload" }, window.location.origin);
  }, [frameRef]);

  const setDesign = (d, usage) => {
    setDesignState(d);
    if (usage) onUsage(usage);
    notifyTool();
  };

  async function apply(body) {
    setBusy(true);
    setFlash("");
    try {
      const d = await projectApi(`/${p.id}/apply`, { method: "POST", body: JSON.stringify(body) });
      setDesign(d.design, d.usage);
      if (body.swap) {
        const { message_id: mid, from_id: from, to_id: to } = body.swap;
        setMsgs((list) => list.map((m) => (m.id !== mid ? m : {
          ...m,
          recommend: (m.recommend || []).map((it) => (it.id === from ? { ...it, id: to, from: it.from || from } : it)),
        })));
      }
      setFlash("연결도에 적용했습니다");
      setTimeout(() => setFlash(""), 2500);
    } catch (e) {
      setFlash(e.message);
    } finally {
      setBusy(false);
    }
  }
  const act = { apply, busy, setDesign };
  const applied = new Set((design?.modules || []).map((m) => m.id));

  const onDrag = useDrag(
    (dx, dy) => setWin(clampWin({ ...start.current, x: start.current.x + dx, y: start.current.y + dy })),
    () => {},
  );
  const onSize = useDrag(
    (dx, dy) => setWin(clampWin({ ...start.current, w: start.current.w + dx, h: start.current.h + dy })),
    () => {},
  );
  const grab = (handler) => (e) => {
    if (e.target.closest("button, a, input")) return;
    start.current = win;
    handler(e);
  };

  if (win.min) {
    return (
      <button type="button" className="lb-win-pill" style={{ left: win.x, top: win.y }}
              onClick={() => setWin((w) => ({ ...w, min: false }))}>
        <LaonaMark size={18} /> 라오나봇 <b>{p.plan?.paid ? "이용료 결제됨" : `무료 ${won(Math.min(planUsed(p), p.plan?.free_krw ?? 0))}`}</b>
      </button>
    );
  }

  const moved = p.status === "MANUAL";
  /* 예전 규칙(시작할 때 선금)으로 만들어 아직 입금 전인 프로젝트 — 이용료 결제 안내를 그대로 쓴다 */
  const pending = p.status === "DEPOSIT_PENDING";
  return (
    <section className="lb-win" style={{ left: win.x, top: win.y, width: win.w, height: win.h }} aria-label="라오나봇 대화창">
      <header className="lb-win__bar" onPointerDown={grab(onDrag)}>
        <LaonaMark size={20} />
        <div className="lb-win__title">
          <b>라오나봇</b>
          <span title={p.title}>{p.title}</span>
        </div>
        <PlanBadge p={p} />
        <Link to="/laonabot/projects" className="lb-win__btn" title="내 프로젝트">≡</Link>
        <button type="button" className="lb-win__btn" title="접기" onClick={() => setWin((w) => ({ ...w, min: true }))}>—</button>
      </header>
      {moved ? (
        <div className="lb-win__scroll"><MovedPanel p={p} /></div>
      ) : pending ? (
        <div className="lb-win__scroll">
          <PlanPanel p={p} plan={{ ...p.plan, fee: p.deposit, pay_requested: !!p.depositor }} bank={p.pay?.bank}
                     reason="선금 입금을 기다리고 있습니다" onChanged={reload} />
        </div>
      ) : (
        <>
          <div className="lb-win__tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === "chat"} onClick={() => setTab("chat")}>대화</button>
            <button type="button" role="tab" aria-selected={tab === "design"} onClick={() => setTab("design")}>
              설계{applied.size ? ` (${applied.size})` : ""}
            </button>
            <button type="button" role="tab" aria-selected={tab === "make"} onClick={() => setTab("make")}>제작</button>
            <StatusTag status={locked || !design?.modules?.length ? status : "DESIGNED"} />
          </div>
          {flash && <p className="lb-win__flash" role="status">{flash}</p>}
          {tab === "chat" && (
            <ChatTab p={p} msgs={msgs} setMsgs={setMsgs} onUsage={onUsage} applied={applied} catalog={catalog} act={act}
                     locked={locked} reload={reload}
                     onSaved={(st) => { setStatus(st); setFlash("내 프로젝트에 저장했습니다"); setTimeout(() => setFlash(""), 4000); }} />
          )}
          {tab === "design" && (
            <DesignTab p={p} design={design} catalog={catalog} act={act} locked={locked} toMake={() => setTab("make")} />
          )}
          {/* 제작 탭은 설계 상태(모듈 유무)를 서버에서 다시 읽는다 */}
          {tab === "make" && <MakeTab p={{ ...p, status }} onUsage={onUsage} onStatus={setStatus} reload={reload} />}
        </>
      )}
      <span className="lb-win__size" onPointerDown={grab(onSize)} aria-hidden="true" />
    </section>
  );
}

export default function LaonaProject() {
  const { id } = useParams();
  const frameRef = useRef(null);
  const [state, setState] = useState({ status: "loading", p: null });

  /* 다시 읽기(결제 확인 폴링 · 402 뒤)가 한 번 실패해도 열려 있는 화면(연결도 포함)은 그대로 둔다 — 404 일 때만 바꾼다 */
  const load = useCallback(async () => {
    try {
      const p = await projectApi(`/${id}`);
      setState({ status: "ready", p });
    } catch (e) {
      setState((s) => (s.p && e.status !== 404 ? s : { status: e.status === 404 ? "missing" : "error", p: null }));
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const p = state.p;
  useSeo({ title: p ? `${p.title} — LaonaBot 프로젝트` : "프로젝트 — LaonaBot", path: `/laonabot/projects/${id}` });
  const setUsage = useCallback((usage) => setState((s) => ({ ...s, p: { ...s.p, usage } })), []);

  if (state.status !== "ready") {
    return (
      <section className="lb-section lb-section--top">
        <div className="lb-wrap">
          <Link to="/laonabot/projects" className="lb-back">← 내 프로젝트</Link>
          {state.status === "loading" && <p className="lb-empty">불러오는 중입니다…</p>}
          {state.status === "error" && <p className="lb-empty">프로젝트를 불러오지 못했습니다.</p>}
          {state.status === "missing" && <div className="lb-empty lb-empty--box"><b>프로젝트를 찾을 수 없습니다</b></div>}
        </div>
      </section>
    );
  }

  return (
    <div className="lb-toolpage">
      {/* 수동 설계와 같은 연결도 화면 — 이 프로젝트의 작업은 도구 안에서 따로 저장된다 */}
      <iframe ref={frameRef} className="lb-toolframe" src={`/api/laona/tool?project=${p.id}`} title={`PinWire — ${p.title}`} />
      {/* 선금 확인 · 직접 만들기로 넘긴 뒤에는 대화 · 설계 상태를 새로 받아 다시 그린다 */}
      <ChatWindow key={["DEPOSIT_PENDING", "MANUAL"].includes(p.status) ? p.status : "open"} p={p} reload={load} onUsage={setUsage} frameRef={frameRef} />
    </div>
  );
}
